import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronLeftIcon, MagnifyingGlassIcon, PlusIcon, ArrowUturnLeftIcon, ArchiveBoxIcon, UserPlusIcon } from '@heroicons/react/24/outline';
import { useSims, useAddSim, useUpdateSim } from '../hooks/useSims';

// SIM manager. Two views over one inventory:
//
//   People    — who holds what, and who is missing a SIM
//   Inventory — every SIM including spare stock, which is ~45% of the estate
//
// Both read the same response so they can never disagree on screen. The company
// directory reads its contact number from this same table, so an assignment
// here shows there immediately.

const STATUS_STYLES = {
  assigned: 'bg-emerald-100 text-emerald-800 ring-emerald-600/30',
  spare: 'bg-sky-100 text-sky-800 ring-sky-600/30',
  retired: 'bg-gray-100 text-gray-600 ring-gray-500/30',
};

function formatPhone(raw) {
  if (!raw) return '—';
  const s = String(raw);
  if (/^\+27\d{9}$/.test(s)) return `+27 ${s.slice(3, 5)} ${s.slice(5, 8)} ${s.slice(8)}`;
  if (/^07\d{9}$/.test(s)) return `${s.slice(0, 5)} ${s.slice(5)}`;
  return s;
}

function StatusChip({ status }) {
  return (
    <span className={`rounded-full px-2 py-0.5 text-xs font-medium ring-1 ring-inset ${STATUS_STYLES[status] || STATUS_STYLES.retired}`}>
      {status}
    </span>
  );
}

// Assigning from the inventory needs a person; assigning from the people view
// needs a SIM. Same picker either way, so the two directions behave alike.
function Picker({ label, options, onPick, onCancel, placeholder }) {
  const [q, setQ] = useState('');
  const matches = useMemo(() => {
    const t = q.trim().toLowerCase();
    if (!t) return options.slice(0, 8);
    return options.filter((o) => o.label.toLowerCase().includes(t)).slice(0, 12);
  }, [q, options]);

  return (
    <div className="rounded-md border border-gray-200 bg-white p-2 shadow-sm">
      <p className="mb-1 text-xs font-medium text-gray-500">{label}</p>
      <input
        autoFocus
        value={q}
        onChange={(e) => setQ(e.target.value)}
        onKeyDown={(e) => e.key === 'Escape' && onCancel()}
        placeholder={placeholder}
        className="w-full rounded-md border border-gray-300 px-2 py-1 text-sm focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand"
      />
      <ul className="mt-1 max-h-48 overflow-y-auto">
        {matches.map((o) => (
          <li key={o.value}>
            <button type="button" onClick={() => onPick(o.value)}
              className="w-full px-2 py-1 text-left text-sm hover:bg-brand-light">
              {o.label}
            </button>
          </li>
        ))}
        {matches.length === 0 ? <li className="px-2 py-2 text-xs text-gray-400">No match.</li> : null}
      </ul>
      <button type="button" onClick={onCancel} className="mt-1 text-xs text-gray-500 underline">Cancel</button>
    </div>
  );
}

function AddSimForm({ onClose }) {
  const [form, setForm] = useState({ phone_number: '', network: 'Vodafone', device_type: 'Voice Sim', holder_label: 'Spare' });
  const [error, setError] = useState(null);
  const add = useAddSim();

  const submit = async () => {
    setError(null);
    try {
      await add.mutateAsync(form);
      onClose();
    } catch (err) {
      setError(err.response?.data?.error || 'Could not add that SIM.');
    }
  };

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  return (
    <div className="mb-4 rounded-lg border border-gray-200 bg-gray-50 p-4">
      <h2 className="mb-3 font-medium text-gray-900">Add a SIM to the inventory</h2>
      <div className="flex flex-wrap gap-3">
        <input value={form.phone_number} onChange={set('phone_number')} placeholder="07xxx xxxxxx or +27..."
          className="w-52 rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand" />
        <input value={form.network} onChange={set('network')} placeholder="Network"
          className="w-36 rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand" />
        <select value={form.device_type} onChange={set('device_type')}
          className="rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand">
          <option>Voice Sim</option><option>Mobile Phone</option><option>Data Sim</option>
        </select>
        <input value={form.holder_label} onChange={set('holder_label')} placeholder="Label, e.g. Spare"
          className="w-40 rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand" />
        <button type="button" onClick={submit} disabled={add.isPending || !form.phone_number.trim()}
          className="rounded-md bg-brand px-4 py-2 text-sm font-medium text-white hover:bg-brand-dark disabled:opacity-50">
          {add.isPending ? 'Adding...' : 'Add'}
        </button>
        <button type="button" onClick={onClose}
          className="rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-white">Cancel</button>
      </div>
      {error ? <p className="mt-2 text-sm text-rose-700">{error}</p> : null}
      <p className="mt-2 text-xs text-gray-500">Added as spare stock. Assign it to someone from either view.</p>
    </div>
  );
}

function SimManager() {
  const { data, isLoading, error } = useSims();
  const update = useUpdateSim();
  const [view, setView] = useState('people');
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [adding, setAdding] = useState(false);
  const [picking, setPicking] = useState(null); // { kind:'sim'|'person', id }
  const [message, setMessage] = useState(null);

  const sims = useMemo(() => data?.sims || [], [data]);
  const people = useMemo(() => data?.people || [], [data]);
  const stats = data?.stats;

  const spareOptions = useMemo(
    () => sims.filter((s) => s.status === 'spare')
      .map((s) => ({ value: s.id, label: `${formatPhone(s.phone_number)} — ${s.holder_label || 'Spare'}${s.network ? ` (${s.network})` : ''}` })),
    [sims]
  );
  const personOptions = useMemo(
    () => people.map((p) => ({ value: p.entra_user_id, label: `${p.display_name}${p.phone_number ? ` — has ${formatPhone(p.phone_number)}` : ''}` })),
    [people]
  );

  const act = async (id, body, note) => {
    setMessage(null);
    try {
      const r = await update.mutateAsync({ id, ...body });
      setPicking(null);
      if (r.returnedToStock) setMessage(`${formatPhone(r.returnedToStock)} went back to spare stock.`);
      else if (note) setMessage(note);
    } catch (err) {
      setMessage(err.response?.data?.error || 'That did not work.');
    }
  };

  if (isLoading) return <div className="p-8"><p className="text-sm text-gray-400">Loading SIMs...</p></div>;
  if (error) {
    return (
      <div className="p-8">
        <p className="text-sm text-rose-700">
          {error.response?.status === 403 ? 'The SIM manager is restricted to Logistics.' : 'Could not load the SIM inventory.'}
        </p>
      </div>
    );
  }

  const filteredSims = sims.filter((s) => {
    if (statusFilter !== 'all' && s.status !== statusFilter) return false;
    const t = search.trim().toLowerCase();
    if (!t) return true;
    return [s.phone_number, s.assigned_name, s.holder_label, s.network, s.device_type]
      .some((v) => String(v ?? '').toLowerCase().includes(t));
  });

  const filteredPeople = people.filter((p) => {
    if (statusFilter === 'spare' && p.phone_number) return false;
    const t = search.trim().toLowerCase();
    if (!t) return true;
    return [p.display_name, p.job_title, p.department, p.phone_number]
      .some((v) => String(v ?? '').toLowerCase().includes(t));
  });

  return (
    <div className="p-4 sm:p-8">
      {/* Internal route, so a router Link rather than the cross-app anchors
          the tech and logistics modules use — same look, no page reload. */}
      <Link
        to="/?dept=logistics"
        className="mb-4 inline-flex items-center gap-1 text-sm font-medium text-brand hover:text-brand-dark"
      >
        <ChevronLeftIcon className="h-4 w-4" />
        Back to Logistics
      </Link>
      <div className="mb-4">
        <h1 className="text-xl font-semibold text-gray-900">SIM manager</h1>
        <p className="mt-1 text-sm text-gray-500">
          {stats.total} SIMs — {stats.assigned} assigned, {stats.spare} spare
          {stats.retired ? `, ${stats.retired} retired` : ''}. {stats.peopleWithout} people have no SIM.
          Changes show in the company directory straight away.
        </p>
      </div>

      <div className="mb-4 flex flex-wrap items-center gap-2">
        <div className="flex rounded-md border border-gray-300">
          {[['people', 'People'], ['inventory', 'Inventory']].map(([k, label]) => (
            <button key={k} type="button" onClick={() => setView(k)}
              className={`px-4 py-2 text-sm font-medium ${view === k ? 'bg-brand text-white' : 'text-gray-700 hover:bg-gray-50'}`}>
              {label}
            </button>
          ))}
        </div>

        <div className="relative min-w-[14rem] flex-1">
          <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search name, number, label..."
            className="w-full rounded-md border border-gray-300 py-2 pl-9 pr-3 text-sm focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand" />
        </div>

        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}
          className="rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand">
          {view === 'inventory'
            ? <>
                <option value="all">All ({stats.total})</option>
                <option value="assigned">Assigned ({stats.assigned})</option>
                <option value="spare">Spare ({stats.spare})</option>
                <option value="retired">Retired ({stats.retired})</option>
              </>
            : <>
                <option value="all">Everyone ({stats.peopleTotal})</option>
                <option value="spare">Without a SIM ({stats.peopleWithout})</option>
              </>}
        </select>

        <button type="button" onClick={() => setAdding((v) => !v)}
          className="flex items-center gap-1.5 rounded-md bg-brand px-4 py-2 text-sm font-medium text-white hover:bg-brand-dark">
          <PlusIcon className="h-4 w-4" /> Add SIM
        </button>
      </div>

      {adding ? <AddSimForm onClose={() => setAdding(false)} /> : null}
      {message ? <p className="mb-3 rounded-md bg-gray-100 px-3 py-2 text-sm text-gray-700">{message}</p> : null}

      <div className="max-h-[70vh] overflow-auto rounded-lg border border-gray-200">
        <table className="min-w-full text-sm">
          <thead>
            <tr className="bg-gray-50">
              {(view === 'people'
                ? ['Person', 'Department', 'SIM', '']
                : ['Number', 'Held by', 'Status', 'Network / type', 'Last change', '']
              ).map((h, i) => (
                <th key={i} className="sticky top-0 z-10 whitespace-nowrap bg-gray-50 px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">{h}</th>
              ))}
            </tr>
          </thead>

          <tbody>
            {view === 'people' && filteredPeople.map((p) => (
              <tr key={p.entra_user_id} className="border-t border-gray-100 hover:bg-gray-50">
                <td className="px-3 py-2">
                  <div className="font-medium text-gray-900">{p.display_name}</div>
                  <div className="text-xs text-gray-500">{p.job_title || '—'}</div>
                </td>
                <td className="px-3 py-2 text-gray-600">{p.department || '—'}</td>
                <td className="px-3 py-2 tabular-nums">
                  {p.phone_number ? formatPhone(p.phone_number) : <span className="text-gray-400">no SIM</span>}
                </td>
                <td className="px-3 py-2 text-right">
                  {picking?.kind === 'sim' && picking.id === p.entra_user_id ? (
                    <Picker label={`Give ${p.display_name.split(' ')[0]} a SIM`} options={spareOptions}
                      placeholder="Search spare SIMs..."
                      onCancel={() => setPicking(null)}
                      onPick={(simId) => act(simId, { action: 'assign', entra_user_id: p.entra_user_id })} />
                  ) : (
                    <div className="flex justify-end gap-1">
                      <button type="button" onClick={() => setPicking({ kind: 'sim', id: p.entra_user_id })}
                        className="flex items-center gap-1 rounded-md border border-gray-300 px-2.5 py-1 text-xs font-medium text-gray-700 hover:bg-gray-50">
                        <UserPlusIcon className="h-3.5 w-3.5" />{p.phone_number ? 'Change' : 'Assign'}
                      </button>
                      {p.phone_number ? (
                        <button type="button" onClick={() => act(p.sim_id, { action: 'unassign' }, `${formatPhone(p.phone_number)} returned to stock.`)}
                          className="rounded-md border border-gray-300 px-2.5 py-1 text-xs font-medium text-gray-700 hover:bg-gray-50">
                          Take back
                        </button>
                      ) : null}
                    </div>
                  )}
                </td>
              </tr>
            ))}

            {view === 'inventory' && filteredSims.map((s) => (
              <tr key={s.id} className="border-t border-gray-100 hover:bg-gray-50">
                <td className="px-3 py-2 font-medium tabular-nums text-gray-900">{formatPhone(s.phone_number)}</td>
                <td className="px-3 py-2 text-gray-600">{s.assigned_name || s.holder_label || '—'}</td>
                <td className="px-3 py-2"><StatusChip status={s.status} /></td>
                <td className="px-3 py-2 text-xs text-gray-500">{[s.network, s.device_type].filter(Boolean).join(' · ') || '—'}</td>
                <td className="px-3 py-2 text-xs text-gray-400">{s.updated_by || '—'}</td>
                <td className="px-3 py-2 text-right">
                  {picking?.kind === 'person' && picking.id === s.id ? (
                    <Picker label={`Assign ${formatPhone(s.phone_number)}`} options={personOptions}
                      placeholder="Search people..." onCancel={() => setPicking(null)}
                      onPick={(entraId) => act(s.id, { action: 'assign', entra_user_id: entraId })} />
                  ) : (
                    <div className="flex justify-end gap-1">
                      {s.status !== 'retired' ? (
                        <button type="button" onClick={() => setPicking({ kind: 'person', id: s.id })}
                          className="rounded-md border border-gray-300 px-2.5 py-1 text-xs font-medium text-gray-700 hover:bg-gray-50">
                          {s.status === 'assigned' ? 'Reassign' : 'Assign'}
                        </button>
                      ) : null}
                      {s.status === 'assigned' ? (
                        <button type="button" onClick={() => act(s.id, { action: 'unassign' })}
                          className="rounded-md border border-gray-300 px-2.5 py-1 text-xs font-medium text-gray-700 hover:bg-gray-50">
                          Take back
                        </button>
                      ) : null}
                      {s.status !== 'retired' ? (
                        <button type="button" onClick={() => act(s.id, { action: 'retire' }, `${formatPhone(s.phone_number)} retired.`)}
                          className="flex items-center gap-1 rounded-md border border-gray-300 px-2.5 py-1 text-xs font-medium text-gray-700 hover:bg-gray-50">
                          <ArchiveBoxIcon className="h-3.5 w-3.5" />Retire
                        </button>
                      ) : (
                        <button type="button" onClick={() => act(s.id, { action: 'restore' }, `${formatPhone(s.phone_number)} back in stock.`)}
                          className="flex items-center gap-1 rounded-md border border-gray-300 px-2.5 py-1 text-xs font-medium text-gray-700 hover:bg-gray-50">
                          <ArrowUturnLeftIcon className="h-3.5 w-3.5" />Restore
                        </button>
                      )}
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <p className="mt-3 text-xs text-gray-400">
        Retiring keeps the number on record rather than deleting it — old stock counts and records that name it still resolve.
      </p>
    </div>
  );
}

export default SimManager;
